import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useCapabilities } from '../../lib/capabilities'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Expense } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, CoinIcon, PlusIcon } from '../../components/icons'

interface ExpensesResponse { ok: boolean; items: Expense[]; categories: [string, string][] }

const FALLBACK_CATEGORIES: [string, string][] = [
  ['fuel', 'Fuel'], ['driver_salary', 'Driver salary'], ['staff_salary', 'Staff salary'],
  ['food', 'Food / per-diem'], ['maintenance', 'Maintenance & repairs'], ['hire_fee', 'Hire fee'],
  ['permit', 'Permits & fees'], ['transport', 'Transport / logistics'],
  ['supplies', 'Consumables / supplies'], ['other', 'Other'],
]

export default function ExpenseNew() {
  /* Capture vs reporting separation - see ops/Sales.tsx for the rationale;
   * expense.log holders capture, reports capabilities read. */
  const caps = useCapabilities()
  const canViewReports = caps.has('*') || caps.has('reports.view.sensitive') || caps.has('reports.view.operations')
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'expenses'],
    queryFn: () => api.get<ExpensesResponse>('/ops/expenses'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.offline.post('/ops/expenses', body, 'Expense'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ops', 'expenses'] }); setShowForm(false) },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      category: form.get('category'),
      amount: Number(form.get('amount') ?? 0),
      currency: form.get('currency') || 'ZMW',
      spent_at: form.get('spent_at'),
      description: form.get('description'),
      payee: form.get('payee'),
      payment_method: form.get('payment_method'),
    })
  }

  const items = data?.items ?? []
  const categories = data?.categories?.length ? data.categories : FALLBACK_CATEGORIES

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Log an expense</h1>
          <p className="page-head__sub">Record an operational cost for your organisation.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Log expense'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Log expense'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Log an expense" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not log that expense.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'What & how much', content: (
                      <div className="grid grid--2">
                        <Select label="Category" name="category" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {categories.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </Select>
                        <Input label="Amount" name="amount" type="number" step="any" min="0" required />
                      </div>
                    ) },
                    { title: 'When & currency', content: (
                      <div className="grid grid--2">
                        <Input label="Date" name="spent_at" type="date" defaultValue={new Date().toISOString().slice(0, 10)} />
                        <Input label="Currency" name="currency" defaultValue="ZMW" />
                      </div>
                    ) },
                    { title: 'Who was paid', content: (
                      <div className="grid grid--2">
                        <Input label="Payee" name="payee" />
                        <Input label="Payment method" name="payment_method" placeholder="e.g. Mobile money" />
                      </div>
                    ) },
                    { title: 'Description', content: (
                      <Input label="Description" name="description" />
                    ) },
                  ]}
                  footer={(
                <div className="row">
                  <Button type="submit" loading={create.isPending} confirm={{ title: "Record this expense?" }}>Log expense</Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {!canViewReports && (
        <Alert tone="info">
          Recorded entries feed your organisation&rsquo;s reports. Ask a leader or an
          admin for reporting access if you need to review past records.
        </Alert>
      )}

      {canViewReports && (
      <Card>
        <CardHeader title="Your expenses" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<CoinIcon size={22} />} title="No expenses logged" body="Expenses you log will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Date</th><th>Category</th><th>Payee</th><th className="table__num">Amount</th></tr></thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDate(e.spent_at)}</td>
                      <td>{titleCase(e.category)}</td>
                      <td>{e.payee || '—'}</td>
                      <td className="table__num">{formatNumber(e.amount, 2)} {e.currency || 'ZMW'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      )}
    </>
  )
}
